import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { isMobile, isTablet } from "react-device-detect";
import {
  APP_STORE_URL,
  GET_APP_LINK,
  PLAY_STORE_URL,
} from "../common/InstallApp/links";
import appleappstore from "../Modals/app-store-logo.png";
import googleplaystore from "../Modals/google-play-logo.png";
import { color, fontWeight } from "../../theme/tokens";
import {
  logGetAppEvent,
  type GetAppEvent,
  type GetAppParams,
} from "../../utils/getAppAnalytics";

/**
 * #1259 (Free Feed, Increment 3, Slice 2): the get-the-app card at the top of
 * My Account, for a member who isn't paying. Board part `FF-1228-Install` as
 * placed on My Account in option C (`tone="ink"`, `size="big"`); the website's
 * tokens (`theme/tokens.ts`) give the styling.
 *
 * #1184: the same card on the episode page (`placement="episode"`), for every
 * signed-in member, paying included (Ellaie, 3 Nov). There it is slim on the
 * darker grey ground (`size="slim"`, `tone="dark"`; boards FF-1184-A and
 * FF-1184-Phone), with its own line and, on a Members Only trailer, a lime
 * line above the heading (`highlight`). The defaults keep My Account as built.
 *
 * **SUBSCRIPTIONS / MEMBERSHIP ESCALATION CLASS** (what a non-member is
 * offered). The card links to the app and the stores only: it starts no trial,
 * takes no payment and changes nothing anyone can watch.
 *
 * Device split, badges and links copied from
 * `Onboarding/SignUp/Steps/SignUpStepGetApp.tsx`; every text colour is named
 * (app-web CLAUDE.md: MUI's defaults assume a light background).
 */

/** The statuses that see the card on My Account (#1259 done-when 1), and
 *  `trial` with its own line (#1261 done-when 9, board FF-1260-B). Not
 *  `standard`, and not `locked` (default taken in the #1259 plan). The episode
 *  page shows the card to every status. */
export const GET_APP_CARD_STATUSES = [
  "standard-incomplete",
  "trial-expired",
  "standard-expired",
  "new-trial-eligible",
  "trial",
];

// Exact words from the ticket ("Decided", Ellaie 3 Nov) and the board.
const HEADING = "Watch FarmFLiX in the app";
const LINE = "All our free episodes are in the FarmFLiX app.";
// #1261 done-when 9: a trial member's line on My Account (board FF-1260-B).
const TRIAL_LINE =
  "Every episode is in the FarmFLiX app until your free trial ends.";
const OPEN_BUTTON = "OPEN IN THE APP";
const OPEN_HELP = "No app yet? This takes you to the store.";
const SEARCH_LINE = "Or search 'FarmFLiX' in your phone's app store";

/** #1184: the episode page's words, as drawn on boards FF-1184-A and
 *  FF-1184-Phone (Ellaie, 3 Nov, ask 83). */
export const EPISODE_GET_APP_LINE =
  "Install the app to get access to all our free episodes.";
export const EPISODE_GET_APP_TRAILER =
  "You're watching the trailer. The full episode is for members.";

type Size = "big" | "slim";
type Tone = "ink" | "dark";
type Placement = "my_account" | "episode";

const EVENT: Record<Placement, GetAppEvent> = {
  my_account: "my_account_get_app_click",
  episode: "episode_get_app_click",
};

// Board: 8px corners (no card radius in the website design system), 12px row
// gap and 24px column gap. Ground: ink (`#171717`) on My Account; the darker
// grey (`#202020`) on the episode page, which is itself `#171717`. Padding:
// big 20px phone / 32px computer; slim 16px phone / 20px computer.
const cardSx =
  (phoneOrTablet: boolean, size: Size, tone: Tone): SxProps<Theme> =>
  (theme) => ({
    backgroundColor:
      tone === "dark" ? "background.ffNavyLight" : "background.ffNavy",
    borderRadius: "8px",
    padding:
      size === "slim"
        ? theme.spacing(phoneOrTablet ? 2 : 2.5)
        : theme.spacing(phoneOrTablet ? 2.5 : 4),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5, 3),
  });

const wordsSx: SxProps<Theme> = (theme) => ({
  flex: "1 1 260px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
});

// Board: big 28.8px weight 500; slim 24px weight 400; white. The theme's h2
// has no breakpoint rules, so the flat size holds at every width.
const headingSx = (size: Size): SxProps<Theme> => ({
  color: color.whiteLong,
  fontSize: size === "slim" ? "1.5rem" : "1.8rem",
  lineHeight: 1.2,
  fontWeight: size === "slim" ? fontWeight.regular : fontWeight.medium,
  margin: 0,
});

// Board: the lime line above the heading, bold, at the body size. 700 as
// elsewhere on the site (tokens.ts has no bold weight).
const highlightSx: SxProps<Theme> = {
  color: color.ffGreen,
  fontWeight: 700,
};

const lineSx: SxProps<Theme> = {
  color: "common.lightgray",
};

const helpSx: SxProps<Theme> = {
  color: "common.darkgray",
};

const openSx: SxProps<Theme> = (theme) => ({
  flex: "1 1 100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.75),
  textAlign: "center",
});

const badgesBlockSx: SxProps<Theme> = (theme) => ({
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: theme.spacing(1),
});

// Copied from SignUpStepGetApp.tsx (`badgesSx`), right-aligned as the board.
const badgesSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
};

// Copied from SignUpStepGetApp.tsx (itself from common/InstallApp/HeroLayout.tsx).
const AppStoreLogo = styled("img")(({ theme }) => ({
  maxWidth: "140px",
  margin: "5px",
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    margin: "10px 5px",
    maxWidth: "130px",
  },
  [theme.breakpoints.up("md")]: {
    margin: "10px 5px",
    maxWidth: "160px",
  },
}));

type GetAppCardProps = {
  /** The signed-in member's status. On My Account the card shows only for
   *  the statuses in GET_APP_CARD_STATUSES; on an episode, for any status. */
  memberStatusId?: string;
  /** The line under the heading. On My Account, left out: the trial line for
   *  a trial member, the free-episodes line for everyone else. */
  line?: string;
  /** `big` on My Account; `slim` on the episode page. */
  size?: Size;
  /** `ink` (`#171717`) on My Account; `dark` (`#202020`) on the episode page. */
  tone?: Tone;
  /** A lime line above the heading, e.g. on a Members Only trailer. */
  highlight?: string;
  /** Where the card is: decides who sees it and the event a tap records. */
  placement?: Placement;
};

const GetAppCard = ({
  memberStatusId,
  line,
  size = "big",
  tone = "ink",
  highlight,
  placement = "my_account",
}: GetAppCardProps) => {
  if (
    !memberStatusId ||
    (placement === "my_account" &&
      !GET_APP_CARD_STATUSES.includes(memberStatusId))
  ) {
    return null;
  }

  // Phones and tablets get the button; everything else (computers and
  // smart-TV browsers) gets the badges. Copied from SignUpStepGetApp.tsx.
  const phoneOrTablet = isMobile || isTablet;

  const logClick = (target: GetAppParams["target"]) =>
    logGetAppEvent(EVENT[placement], {
      member_status: memberStatusId,
      device_type: phoneOrTablet ? "phone" : "computer",
      target,
    });

  return (
    <Box
      component="section"
      aria-label={HEADING}
      sx={cardSx(phoneOrTablet, size, tone)}
    >
      <Box sx={wordsSx}>
        {highlight && (
          <Typography variant="body1" sx={highlightSx}>
            {highlight}
          </Typography>
        )}
        <Typography variant="h2" sx={headingSx(size)}>
          {HEADING}
        </Typography>
        <Typography variant="body1" sx={lineSx}>
          {line ?? (memberStatusId === "trial" ? TRIAL_LINE : LINE)}
        </Typography>
      </Box>

      {phoneOrTablet ? (
        <Box sx={openSx}>
          <Button
            variant="contained"
            color="primary"
            href={GET_APP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logClick("open")}
          >
            {OPEN_BUTTON}
          </Button>
          <Typography variant="body2" sx={helpSx}>
            {OPEN_HELP}
          </Typography>
        </Box>
      ) : (
        <Box sx={badgesBlockSx}>
          <Box sx={badgesSx}>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logClick("app_store")}
            >
              <AppStoreLogo src={appleappstore} alt="Apple App Store Graphic" />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => logClick("google_play")}
            >
              <AppStoreLogo
                src={googleplaystore}
                alt="Google Play Store Graphic"
              />
            </a>
          </Box>
          <Typography variant="body2" sx={helpSx}>
            {SEARCH_LINE}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GetAppCard;
